// Delivery tab — what actually happened to each bulk notification (wave 3,
// W2 look: KPI tiles, then the campaigns in the modern table with a detail
// row per campaign).
//
// This is the view that did not exist. Bulk push was queued into /packets as
// `{chunk, uids, data}` with no campaign id, no state and no counters, so once
// an admin hit Send the send was invisible: you could not tell whether it had
// gone out, how far it had got, why it had stopped, or who had been missed. The
// old Notification Metrics tab read /notification_metrics, which recorded
// `push.sent = tokens.length` the moment the work was QUEUED — so it reported
// thousands of deliveries that had not happened.
//
// A campaign record carries the real numbers, maintained by the delivery workers
// with atomic increments as each batch settles. Progress is measured against the
// AUDIENCE (plan.recipients) rather than against attempts, so a retry wave never
// makes the denominator drift: "1,842 of 2,000 delivered, 158 outstanding" means
// exactly that, whatever it took to get there.
//
// Cost: one live listener over the last 25 campaign aggregates (~600 B each),
// detached the moment the tab loses focus. Recipient slices and the
// per-recipient ledger are never readable by a browser. Stop and Retry call
// the same endpoints as before (cancelCampaign / retryCampaign) after a
// question, for staff who may send notifications.
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { api } from 'common';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ErrorOutlineRoundedIcon from '@mui/icons-material/ErrorOutlineRounded';
import HourglassEmptyRoundedIcon from '@mui/icons-material/HourglassEmptyRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import BlockRoundedIcon from '@mui/icons-material/BlockRounded';
import BuildOutlinedIcon from '@mui/icons-material/BuildOutlined';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import SmsOutlinedIcon from '@mui/icons-material/SmsOutlined';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import StopCircleOutlinedIcon from '@mui/icons-material/StopCircleOutlined';
import ScheduleRoundedIcon from '@mui/icons-material/ScheduleRounded';
import CampaignOutlinedIcon from '@mui/icons-material/CampaignOutlined';
import MarkEmailReadOutlinedIcon from '@mui/icons-material/MarkEmailReadOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import moment from 'moment/min/moment-with-locales';
import { tokens as c, iconHues } from '../theme';
import { canManagePushNotifications } from '../common/notificationSecurity';
import { formatAddisDateTime } from '../common/exportUtils';
import { showToast, confirmDialog } from '../components/feedback';
import DashboardCard from '../components/DashboardCard';
import { DataTable, StatusChip, InfoRow, formatNumber } from '../components/modern';

// A campaign's state, as the engine sets it.
const STATE_CHIPS = {
  building: { key: 'campaign_state_preparing', label: 'Preparing', tone: 'neutral', icon: <BuildOutlinedIcon /> },
  sending: { key: 'campaign_state_sending', label: 'Sending', tone: 'warning', icon: <HourglassEmptyRoundedIcon /> },
  done: { key: 'delivered', label: 'Delivered', tone: 'success', icon: <CheckRoundedIcon /> },
  partial: { key: 'partly_delivered', label: 'Partly delivered', tone: 'amber', icon: <WarningAmberRoundedIcon /> },
  failed: { key: 'failed', label: 'Failed', tone: 'error', icon: <ErrorOutlineRoundedIcon /> },
  cancelled: { key: 'stopped', label: 'Stopped', tone: 'neutral', icon: <BlockRoundedIcon /> },
};

// Plain-language reasons, so a call-centre supervisor can act without reading
// Expo's docs. Keys are the engine's bounded error vocabulary; each shows
// t('campaign_err_' + code) with this English as the default.
const ERROR_LABELS = {
  DeviceNotRegistered: 'App removed from the device — will never be retried',
  MessageTooBig: 'Message too long for push',
  MessageRateExceeded: 'Provider rate limit — retried automatically',
  MismatchSenderId: 'Push credentials mismatch — needs a developer',
  InvalidCredentials: 'Push credentials invalid — needs a developer',
  timeout: 'Provider did not answer in time',
  unreachable: 'Could not reach the provider',
  http_4xx: 'Provider rejected the request',
  http_5xx: 'Provider error — retried automatically',
  parse_error: 'Unreadable provider response',
  no_receipt: 'Provider returned no receipt',
  batch_too_large: 'Internal batching error',
  provider_unreachable: 'SMS gateway unreachable — retried automatically',
  provider_error: 'Provider reported an error',
  exception: 'Unexpected error',
  cancelled: 'Stopped before sending',
  retried: 'Superseded by a retry',
};

const audienceOf = (campaign, channel) => {
  const planned = campaign.plan && campaign.plan[channel] && campaign.plan[channel].recipients;
  if (typeof planned === 'number') return planned;
  // Pre-plan (still preparing) or a legacy record: fall back to attempts.
  return (campaign[channel] && campaign[channel].total) || 0;
};

const deliveredOf = (campaign, channel) => (campaign[channel] && campaign[channel].sent) || 0;

const totalsOf = (campaign) => {
  const audience = audienceOf(campaign, 'push') + audienceOf(campaign, 'sms');
  const delivered = deliveredOf(campaign, 'push') + deliveredOf(campaign, 'sms');
  return { audience, delivered, outstanding: Math.max(0, audience - delivered) };
};

const isInFlight = (campaign) => campaign.state === 'sending' || campaign.state === 'building';

// A purged campaign has no ledger left to retry FROM (the working data is
// cleared after the retention window), so offering the button would only
// produce a confusing refusal.
const isRetryable = (campaign) => totalsOf(campaign).outstanding > 0 && !isInFlight(campaign) && !campaign.purgedAt;

function ChannelProgress({ channel, campaign }) {
  const { t } = useTranslation();
  const audience = audienceOf(campaign, channel);
  const delivered = deliveredOf(campaign, channel);
  if (audience === 0) {
    return (
      <Box component="span" sx={{ color: c.TEXT_3 }}>
        —
      </Box>
    );
  }
  const pct = Math.min(100, Math.round((delivered / audience) * 100));
  const outstanding = Math.max(0, audience - delivered);
  const complete = outstanding === 0;
  const hue = channel === 'push' ? iconHues.accent : iconHues.info;
  const Icon = channel === 'push' ? NotificationsActiveOutlinedIcon : SmsOutlinedIcon;
  return (
    <Box sx={{ minWidth: 150 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5, flexWrap: 'wrap' }}>
        <Icon aria-hidden="true" sx={{ fontSize: 18, color: hue.fg }} />
        <Box component="span" sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {formatNumber(delivered)} / {formatNumber(audience)}
        </Box>
        {!complete ? (
          <Box component="span" sx={{ fontSize: 12.5, color: c.TEXT_3, whiteSpace: 'nowrap' }}>
            {t('campaign_left', { n: formatNumber(outstanding), defaultValue: '({{n}} left)' })}
          </Box>
        ) : null}
      </Box>
      <LinearProgress
        variant="determinate"
        value={pct}
        color={complete ? 'success' : 'warning'}
        aria-label={`${channel === 'push' ? t('push', { defaultValue: 'Push' }) : t('sms', { defaultValue: 'SMS' })} ${pct}%`}
        sx={{ height: 6, borderRadius: 3, bgcolor: c.SURFACE_ALT }}
      />
    </Box>
  );
}

function CampaignDetail({ campaign, t }) {
  const errors = campaign.errors || {};
  const errorRows = Object.keys(errors)
    .filter((code) => code !== 'retried' && errors[code] > 0)
    .sort((a, b) => errors[b] - errors[a]);
  const audienceStats = campaign.audience || {};
  const duplicatesCollapsed = (audienceStats.duplicateDevices || 0) + (audienceStats.duplicatePhones || 0);
  const { outstanding } = totalsOf(campaign);

  const elapsed =
    campaign.finishedAt && campaign.startedAt
      ? moment.duration(campaign.finishedAt - campaign.startedAt).humanize()
      : campaign.startedAt
        ? moment.duration(Date.now() - campaign.startedAt).humanize()
        : null;

  const heading = { fontSize: 12.5, fontWeight: 700, color: c.TEXT_3, mb: 0.5 };
  const num = (n) => (
    <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
      {formatNumber(n)}
    </Box>
  );

  return (
    <Box sx={{ px: { xs: 0.5, md: 1 } }}>
      {campaign.body ? (
        <Typography variant="body2" sx={{ mb: 1.5, whiteSpace: 'pre-wrap', color: c.INK }}>
          {campaign.body}
        </Typography>
      ) : null}
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' } }}>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h3" sx={heading}>
            {t('audience', { defaultValue: 'Audience' })}
          </Typography>
          <Box component="dl" sx={{ m: 0 }}>
            <InfoRow label={t('campaign_matched', { defaultValue: 'Matched the target' })}>{num(audienceStats.matched || 0)}</InfoRow>
            {duplicatesCollapsed > 0 ? (
              <InfoRow
                label={
                  <Tooltip
                    title={t('campaign_duplicates_tip', {
                      defaultValue: 'Accounts sharing a device or a phone number. Collapsed to one message each — this is why nobody gets the same notification twice.',
                    })}
                  >
                    <span>{t('campaign_duplicates_collapsed', { defaultValue: 'Duplicates collapsed' })}</span>
                  </Tooltip>
                }
              >
                {num(duplicatesCollapsed)}
              </InfoRow>
            ) : null}
            {(audienceStats.placeholderTokens || 0) > 0 ? (
              <InfoRow label={t('campaign_no_push_token', { defaultValue: 'No usable push token' })}>{num(audienceStats.placeholderTokens || 0)}</InfoRow>
            ) : null}
            {(audienceStats.noToken || 0) > 0 ? (
              <InfoRow label={t('campaign_app_not_installed', { defaultValue: 'App not installed' })}>{num(audienceStats.noToken || 0)}</InfoRow>
            ) : null}
            {(audienceStats.smsSuppressedByPush || 0) > 0 ? (
              <InfoRow label={t('campaign_sms_skipped', { defaultValue: 'SMS skipped (reached by push)' })}>{num(audienceStats.smsSuppressedByPush || 0)}</InfoRow>
            ) : null}
          </Box>
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography component="h3" sx={heading}>
            {errorRows.length > 0
              ? t('campaign_why_missed', { defaultValue: 'Why recipients were missed' })
              : t('campaign_progress', { defaultValue: 'Progress' })}
          </Typography>
          <Box component="dl" sx={{ m: 0 }}>
            {errorRows.map((code) => (
              <InfoRow key={code} label={ERROR_LABELS[code] ? t('campaign_err_' + code, { defaultValue: ERROR_LABELS[code] }) : code}>
                {num(errors[code])}
              </InfoRow>
            ))}
            <InfoRow label={t('campaign_batches_done', { defaultValue: 'Batches completed' })}>
              <Box component="span" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {(campaign.tasks && campaign.tasks.done) || 0} / {(campaign.tasks && campaign.tasks.total) || 0}
              </Box>
            </InfoRow>
            {elapsed ? (
              <InfoRow label={campaign.finishedAt ? t('campaign_took', { defaultValue: 'Took' }) : t('campaign_running_for', { defaultValue: 'Running for' })}>
                {elapsed}
              </InfoRow>
            ) : null}
            <InfoRow label={t('campaign_last_activity', { defaultValue: 'Last activity' })}>
              {campaign.lastActivityAt ? (
                <Tooltip title={formatAddisDateTime(campaign.lastActivityAt)}>
                  <span>{moment(campaign.lastActivityAt).fromNow()}</span>
                </Tooltip>
              ) : null}
            </InfoRow>
          </Box>
          {campaign.lastError ? (
            <Typography variant="body2" sx={{ display: 'block', mt: 1, color: c.DANGER_TEXT, overflowWrap: 'anywhere' }}>
              {campaign.lastError}
            </Typography>
          ) : null}
        </Box>
      </Box>
      {campaign.purgedAt && outstanding > 0 ? (
        <Typography variant="body2" sx={{ display: 'block', mt: 1.5, color: c.TEXT_3 }}>
          {t('campaign_purged', {
            defaultValue:
              'Delivery details for this campaign have been cleared (older than the retention window). To reach the remaining recipients, send it again as a new notification.',
          })}
        </Typography>
      ) : null}
      <Typography variant="caption" sx={{ display: 'block', mt: 1.5, color: c.TEXT_3, overflowWrap: 'anywhere' }}>
        {t('campaign_id_line', { id: campaign.id, defaultValue: 'Campaign {{id}}' })}
        {campaign.scheduledNotificationId ? ' · ' + t('campaign_scheduled_record', { id: campaign.scheduledNotificationId, defaultValue: 'scheduled record {{id}}' }) : ''}
      </Typography>
    </Box>
  );
}

export default function CampaignDelivery() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const auth = useSelector((state) => state.auth);
  const canManage = canManagePushNotifications(auth.profile);
  const notificationdata = useSelector((state) => state.notificationdata);
  const campaigns = notificationdata.campaigns;
  const loading = notificationdata.campaignsLoading;

  const [busyId, setBusyId] = useState(null);

  // Live for as long as the tab is mounted, detached on unmount — an idle
  // dashboard must not keep streaming delivery updates nobody is reading.
  useEffect(() => {
    const unsubscribe = dispatch(api.watchCampaigns(25));
    return () => {
      if (typeof unsubscribe === 'function') unsubscribe();
    };
  }, [dispatch]);

  const totals = useMemo(() => {
    const list = campaigns || [];
    return list.reduce(
      (acc, cmp) => {
        const audience = audienceOf(cmp, 'push') + audienceOf(cmp, 'sms');
        acc.campaigns++;
        acc.delivered += deliveredOf(cmp, 'push') + deliveredOf(cmp, 'sms');
        acc.outstanding += Math.max(0, audience - (deliveredOf(cmp, 'push') + deliveredOf(cmp, 'sms')));
        acc.duplicatesCollapsed += ((cmp.audience && cmp.audience.duplicateDevices) || 0) + ((cmp.audience && cmp.audience.duplicatePhones) || 0);
        if (cmp.state === 'sending' || cmp.state === 'building') acc.inFlight++;
        return acc;
      },
      { campaigns: 0, delivered: 0, outstanding: 0, duplicatesCollapsed: 0, inFlight: 0 }
    );
  }, [campaigns]);

  const L = (key, dflt, opts) => t(key, { defaultValue: dflt, ...(opts || {}) });

  const act = async (campaign, kind) => {
    const ok = await confirmDialog({
      title:
        kind === 'cancel'
          ? t('campaign_stop_confirm', { defaultValue: 'Stop this campaign?' })
          : t('campaign_retry_confirm', { defaultValue: 'Retry undelivered recipients?' }),
      body:
        kind === 'cancel'
          ? t('campaign_stop_confirm_body', {
              defaultValue: 'Delivery stops within a few seconds. Recipients already reached keep the message; the rest are not sent.',
            })
          : t('campaign_retry_confirm_body', {
              defaultValue:
                'Only recipients with no successful delivery are re-queued. Anyone who already received this notification will not get it again, and devices the app was removed from are skipped.',
            }),
      confirmLabel: kind === 'cancel' ? t('campaign_stop_button', { defaultValue: 'Stop sending' }) : t('retry'),
      cancelLabel: L('confirm_cancel', 'Cancel'),
      destructive: kind === 'cancel',
    });
    if (!ok) return;
    setBusyId(campaign.id);
    try {
      const result = kind === 'cancel' ? await api.cancelCampaign(campaign.id) : await api.retryCampaign(campaign.id);
      if (kind === 'cancel') {
        if (result.success) showToast.success(t('campaign_stopped_notice', { defaultValue: 'Campaign stopped.' }));
        else showToast.error(result.message || t('campaign_stop_failed', { defaultValue: 'Could not stop this campaign.' }));
      } else {
        const requeued = (result.push || 0) + (result.sms || 0);
        if (requeued > 0) {
          showToast.success(
            t('campaign_requeued_notice', { n: formatNumber(requeued), defaultValue: 'Re-queued {{n}} recipient(s).' }) +
              (result.skippedPermanent
                ? ' ' + t('campaign_skipped_removed_notice', { n: formatNumber(result.skippedPermanent), defaultValue: 'Skipped {{n}} device(s) the app was removed from.' })
                : '')
          );
        } else {
          showToast.info(t('campaign_nothing_to_retry', { defaultValue: 'Nothing left to retry — every remaining failure is permanent.' }));
        }
      }
    } catch (err) {
      showToast.error(err.message || t('campaign_request_failed', { defaultValue: 'Request failed.' }));
    } finally {
      setBusyId(null);
    }
  };

  const stateOf = (campaign) => {
    const chip = STATE_CHIPS[campaign.state] || { label: campaign.state || t('unknown'), tone: 'neutral' };
    return { tone: chip.tone, icon: chip.icon, label: chip.key ? t(chip.key, { defaultValue: chip.label }) : chip.label };
  };

  const deviceWord = (d) => (d === 'ANDROID' ? t('android') : d === 'IOS' ? t('ios') : d);
  const audienceText = (campaign) =>
    [campaign.usertype ? t(campaign.usertype, { defaultValue: campaign.usertype }) : '—', campaign.devicetype && campaign.devicetype !== 'All' ? deviceWord(campaign.devicetype) : null]
      .filter(Boolean)
      .join(' · ');

  const columns = [
    {
      id: 'title',
      title: t('notification', { defaultValue: 'Notification' }),
      field: 'title',
      sortValue: (row) => row.createdAt || 0,
      searchValue: (row) => `${row.title || ''} ${row.body || ''}`,
      render: (campaign) => (
        <Box sx={{ minWidth: 200, maxWidth: 360 }}>
          <Box sx={{ fontWeight: 600, color: c.INK, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {campaign.title || t('campaign_no_title', { defaultValue: '(no title)' })}
          </Box>
          <Box sx={{ fontSize: 12.5, color: c.TEXT_3, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {formatAddisDateTime(campaign.createdAt)}
            {campaign.source === 'scheduled' ? (
              <Tooltip title={t('campaign_scheduled_tip', { defaultValue: 'Fired from a scheduled notification' })}>
                <ScheduleRoundedIcon aria-label={t('campaign_scheduled_tip', { defaultValue: 'Fired from a scheduled notification' })} sx={{ fontSize: 16 }} />
              </Tooltip>
            ) : null}
          </Box>
        </Box>
      ),
    },
    {
      id: 'audience',
      title: t('audience', { defaultValue: 'Audience' }),
      field: 'usertype',
      sortValue: audienceText,
      searchValue: audienceText,
      render: (campaign) => (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          <StatusChip tone="neutral" label={campaign.usertype ? t(campaign.usertype, { defaultValue: campaign.usertype }) : '—'} />
          {campaign.devicetype && campaign.devicetype !== 'All' ? <StatusChip tone="neutral" label={deviceWord(campaign.devicetype)} /> : null}
        </Box>
      ),
    },
    {
      id: 'push',
      title: t('push', { defaultValue: 'Push' }),
      sortValue: (row) => deliveredOf(row, 'push'),
      searchable: false,
      render: (campaign) => <ChannelProgress channel="push" campaign={campaign} />,
    },
    {
      id: 'sms',
      title: t('sms', { defaultValue: 'SMS' }),
      sortValue: (row) => deliveredOf(row, 'sms'),
      searchable: false,
      render: (campaign) => <ChannelProgress channel="sms" campaign={campaign} />,
    },
    {
      id: 'state',
      title: t('status', { defaultValue: 'Status' }),
      field: 'state',
      sortValue: (row) => stateOf(row).label,
      searchValue: (row) => stateOf(row).label,
      render: (campaign) => {
        const st = stateOf(campaign);
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 0.5 }}>
            <StatusChip tone={st.tone} icon={st.icon} label={st.label} />
            {(campaign.autoRetryWaves || 0) > 0 ? (
              <Tooltip
                title={t('campaign_auto_retried_tip', {
                  n: campaign.autoRetryWaves,
                  defaultValue: 'Automatically retried {{n}} time(s) for transient provider errors',
                })}
              >
                <span>
                  <StatusChip tone="neutral" icon={<ReplayRoundedIcon />} label={t('campaign_auto_retry_chip', { n: campaign.autoRetryWaves, defaultValue: 'auto-retry ×{{n}}' })} />
                </span>
              </Tooltip>
            ) : null}
            {busyId === campaign.id ? <CircularProgress size={16} aria-label={L('loading', 'Loading…')} sx={{ ml: 0.5 }} /> : null}
          </Box>
        );
      },
    },
  ];

  const actions = canManage
    ? [
        {
          key: 'retry',
          label: t('retry'),
          icon: <ReplayRoundedIcon />,
          inline: true,
          show: isRetryable,
          disabled: (row) => busyId === row.id,
          tooltip: (row) =>
            t('campaign_retry_tip', {
              n: formatNumber(totalsOf(row).outstanding),
              defaultValue: 'Retry the {{n}} recipient(s) not yet delivered. Already-delivered recipients are never re-sent.',
            }),
          onClick: (row) => act(row, 'retry'),
        },
        {
          key: 'stop',
          label: t('campaign_stop_tip', { defaultValue: 'Stop sending now' }),
          icon: <StopCircleOutlinedIcon />,
          inline: true,
          destructive: true,
          show: isInFlight,
          disabled: (row) => busyId === row.id,
          onClick: (row) => act(row, 'cancel'),
        },
      ]
    : [];

  const firstLoad = loading && !campaigns;

  return (
    <Box dir={i18n.dir()} sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(3, minmax(0, 1fr))', lg: 'repeat(5, minmax(0, 1fr))' } }}>
        <DashboardCard icon={<CampaignOutlinedIcon />} hue="accent" title={t('campaign_stat_campaigns', { defaultValue: 'Recent campaigns' })} value={formatNumber(totals.campaigns)} loading={firstLoad} />
        <DashboardCard icon={<MarkEmailReadOutlinedIcon />} hue="success" title={t('campaign_stat_delivered', { defaultValue: 'Messages delivered' })} value={formatNumber(totals.delivered)} loading={firstLoad} />
        <DashboardCard
          icon={<PendingActionsOutlinedIcon />}
          hue={totals.outstanding > 0 ? 'amber' : 'neutral'}
          title={t('campaign_stat_outstanding', { defaultValue: 'Not yet delivered' })}
          value={formatNumber(totals.outstanding)}
          loading={firstLoad}
        />
        <DashboardCard icon={<ContentCopyOutlinedIcon />} hue="info" title={t('campaign_stat_duplicates', { defaultValue: 'Duplicates prevented' })} value={formatNumber(totals.duplicatesCollapsed)} loading={firstLoad} />
        <DashboardCard icon={<SendOutlinedIcon />} hue="accent" title={t('campaign_stat_inflight', { defaultValue: 'Sending now' })} value={formatNumber(totals.inFlight)} loading={firstLoad} />
      </Box>

      <DataTable
        title={t('delivery', { defaultValue: 'Delivery' })}
        rows={campaigns || []}
        columns={columns}
        loading={firstLoad}
        countText={({ shown, total, query, loading: busy }) =>
          busy
            ? L('loading', 'Loading…')
            : query
              ? L('table_count_shown', '{{shown}} of {{total}}', { shown, total })
              : t('campaigns_count', {
                  count: total,
                  defaultValue_one: '{{count}} campaign',
                  defaultValue_other: '{{count}} campaigns',
                  defaultValue: '{{count}} campaigns',
                })
        }
        searchPlaceholder={L('notifications_search', 'Search title or text')}
        rowLabel={(row) => row.title}
        detailPanel={(campaign) => <CampaignDetail campaign={campaign} t={t} />}
        actions={actions}
        prefsKey="campaigns"
        paging={false}
        densityToggle={false}
        maxHeight={null}
        emptyState={({ query }) =>
          query
            ? null
            : {
                icon: <CampaignOutlinedIcon />,
                title: t('campaign_none', {
                  defaultValue: 'No bulk notifications have been sent yet. Every send from here on appears in this list with live delivery progress.',
                }),
              }
        }
        minWidth={880}
      />
    </Box>
  );
}
